<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idLoteFabricacao: 0,
      idProduto: null,
      nomeProduto: '',
      skuProduto: '',
      idFornecedor: null,
      nomeFornecedor: '',
      numeroLoteFornecedor: '',
      lstStatusLoteFabricacao: '',
      dataFabricacao: '',
      dataValidade: '',
      isBloqueadoParaUso: false,
      idOrganizacao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const mostrarAuditoria = ref(false)
const isLoadingListas = ref(false)

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

const accordionState = reactive({
  capa: true
})

const listas = reactive({})

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  titulo: 'Buscar Produto',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaFornecedor = computed(() => ({
  ...fornecedorSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva,
    isFornecedor: true
  }
}))

// Detecta dinamicamente se a lista usa idListaSistemaItem ou idListaClienteItem
const optionValueStatusField = computed(() => {
  const items = listas.LstStatusLoteFabricacao || []
  if (items.length === 0) return 'idListaSistemaItem'
  if (items[0].idListaSistemaItem !== undefined) return 'idListaSistemaItem'
  if (items[0].idListaClienteItem !== undefined) return 'idListaClienteItem'
  return 'id'
})

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    // Usa o nome da classe C# (LoteFabricacao) que é o mesmo usado no DicionarioMetadadosListas
    const result = await metadataStore.carregarListasParaTabelas(['LoteFabricacao'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

const salvar = async () => {
  if (isReadOnly.value) return

  if (item.value.dataValidade && item.value.dataFabricacao) {
    if (new Date(item.value.dataValidade) <= new Date(item.value.dataFabricacao)) {
      emit('show-notification', { message: 'A Data de Validade deve ser maior que a Data de Fabricação.', type: 'warning' })
      return
    }
  }

  const payload = {
    ...item.value,
    idProduto: item.value.idProduto ? Number(item.value.idProduto) : null,
    idFornecedor: item.value.idFornecedor ? Number(item.value.idFornecedor) : null
  }

  await submitForm('LotesFabricacao', 'idLoteFabricacao', payload)
}

// Ao editar, busca os dados do fornecedor para preencher nomeFornecedor
watch(() => item.value?.idFornecedor, async (idFornecedor) => {
  if (idFornecedor && isEditing.value) {
    try {
      const { data } = await api.get(`/Parceiros/${idFornecedor}`)
      if (data) {
        item.value.nomeFornecedor = data.nomeFantasia ?? ''
      }
    } catch (e) {
      console.warn('Erro ao carregar dados do fornecedor:', e)
    }
  }
}, { immediate: true })

onMounted(() => {
  carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Lote de Fabricação"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @cancelar="handleCancel"
    :desabilitar-salvar="isReadOnly"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- FRAME ÚNICO: CAPA -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.capa = !accordionState.capa">
        <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Dados do Lote</h5>
        <i :class="['bi', accordionState.capa ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.capa">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoBusca id="idProduto" label="Produto" v-model="item.idProduto" :configuracao-busca="configBuscaProduto" required :disabled="isReadOnly" @selecionado="val => { item.idProduto = val.idProduto; item.nomeProduto = val.nome; item.skuProduto = val.sku; item.lstNaturezaItem = val.lstNaturezaItem || ''; item.idTipoProduto = val.idTipoProduto || null; item.nomeTipoProduto = val.nomeTipoProduto || '' }" @show-notification="p => emit('show-notification', p)" />
          </div>

          <div class="col-md-6">
            <CampoBusca id="idFornecedor" label="Fornecedor" v-model="item.idFornecedor" :configuracao-busca="configBuscaFornecedor" required :disabled="isReadOnly" @selecionado="val => item.nomeFornecedor = val.nomeFantasia" @show-notification="p => emit('show-notification', p)" />
          </div>

          <div class="col-md-4">
            <CampoInput id="numeroLoteFornecedor" label="Nº Lote do Fornecedor" v-model="item.numeroLoteFornecedor" placeholder="Ex: LOTE-2025-001" required :readonly="isReadOnly" />
          </div>

          <div class="col-md-4">
            <CampoSelect id="lstStatusLoteFabricacao" label="Status do Lote" v-model="item.lstStatusLoteFabricacao" :options="listas.LstStatusLoteFabricacao || []" option-label="textoLista" :option-value="optionValueStatusField" required :disabled="isReadOnly" />
          </div>

          <div class="col-md-4">
            <CampoSelect id="isBloqueadoParaUso" label="Bloqueado para Uso?" v-model="item.isBloqueadoParaUso" :options="simNaoOptions" option-label="label" option-value="id" :disabled="isReadOnly" descricao-informativa="Bloqueia o lote em todo o sistema." />
          </div>

          <div class="col-md-4">
            <CampoInput id="dataFabricacao" label="Data de Fabricação" type="date" v-model="item.dataFabricacao" required :readonly="isReadOnly" />
          </div>

          <div class="col-md-4">
            <CampoInput id="dataValidade" label="Data de Validade" type="date" v-model="item.dataValidade" required :readonly="isReadOnly" />
          </div>
        </div>
      </div>
    </div>

  </FormularioCadastro>

  <!-- Componente de Visualização do Histórico -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="LoteFabricacao"
    :chave-primaria="item.idLoteFabricacao"
    :titulo-registro="`Lote ${item.idLoteFabricacao}`"
    contexto="tenant"
    :dicionarios="{ 'LstStatusLoteFabricacao': listas.LstStatusLoteFabricacao }"
    @fechar="mostrarAuditoria = false"
  />
</template>
